// Friends.js
// -----------------------------------------------------------------------------
// Freunde-System ("دوستان") - rein additive UI-Bausteine, bewusst ausserhalb
// von App.js, damit die Spiellogik dort unberuehrt bleibt:
//
//  - FriendsModal          Liste "Gefolgt" inkl. Live-Online-Status
//  - AddFriendDialog       Suche mit Namensvorschlaegen (+ افزودن)
//  - PlayerProfileModal    Mini-Profil beim Tipp auf einen Avatar am Tisch
//  - OnlineVisibilityToggle  Privatsphaere "نمایش وضعیت آنلاین من" (Profil)
//  - FriendsIcon           Personen-Icon fuer die Einstiegs-Buttons
//
// Alle Texte persisch/RTL. Daten kommen per REST (server/friends.js), der
// Live-Status per Socket-Event "friends:presence" (nur an Follower).
// Die Sheets liegen mit z-index 390/395 bewusst UNTER den Spiel-Sheets
// (Bieten, Boden, Spielende: 400) - ein offenes Freunde-Fenster kann damit
// nie ein Spiel-Popup verdecken.
// -----------------------------------------------------------------------------

import React, { useCallback, useEffect, useRef, useState } from "react";
import "./friends.css";

export const PRESENCE_EVENT = "friends:presence";
const SEARCH_MIN_LEN = 2;
const SEARCH_DEBOUNCE_MS = 300;
const LIST_REFRESH_MS = 30000; // nur fuer "در بازی" - online/offline kommt live

const STATUS_LABEL = { online: "آنلاین", inGame: "در بازی", offline: "آفلاین" };
const STATUS_ORDER = { inGame: 0, online: 1, offline: 2 };

const faNum = (n) =>
  String(n ?? "").replace(/\d/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[d]);

export function sortFriends(list) {
  return [...list].sort(
    (a, b) =>
      (STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9) ||
      String(a.username || a.name || "").localeCompare(String(b.username || b.name || ""))
  );
}

const displayName = (u) => (u && (u.username || u.name)) || "بازیکن";

async function call(apiBase, token, method, path, body, signal) {
  const res = await fetch(`${apiBase}${path}`, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body ? { "Content-Type": "application/json" } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
    signal,
  });
  let data = null;
  try {
    data = await res.json();
  } catch {
    data = null;
  }
  if (!res.ok) {
    const err = new Error(data?.error || `خطا (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return data || {};
}

const netError = (e) =>
  e?.name === "TypeError" ? "سرور در دسترس نیست" : e?.message || "خطای ناشناخته";

// Escape schliesst das jeweils oberste Fenster.
function useEscape(onClose) {
  const ref = useRef(onClose);
  useEffect(() => {
    ref.current = onClose;
  }, [onClose]);
  useEffect(() => {
    const h = (e) => {
      if (e.key === "Escape") ref.current && ref.current();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);
}

export function FriendsIcon({ size = 20 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-hidden
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ display: "block", flex: "none" }}
    >
      <circle cx="9" cy="8" r="3.5" />
      <path d="M2.5 20c0-3.6 2.9-6.2 6.5-6.2s6.5 2.6 6.5 6.2" />
      <circle cx="17" cy="9" r="2.8" />
      <path d="M16.2 13.9c3 .2 5.3 2.5 5.3 5.6" />
    </svg>
  );
}

function Ava({ apiBase, user, size = 44, status }) {
  const raw = user && user.avatarUrl;
  const resolved = raw
    ? raw.startsWith("http") || raw.startsWith("data:")
      ? raw
      : `${apiBase}${raw}`
    : null;
  const [badSrc, setBadSrc] = useState(null);
  const src = resolved && resolved !== badSrc ? resolved : null;
  const initials = String(displayName(user)).trim().slice(0, 2);
  return (
    <span
      className={"sh-fr-ava" + (status ? " is-" + status : "")}
      style={{ width: size, height: size, fontSize: Math.max(11, Math.round(size / 3.2)) }}
    >
      {src ? (
        <img src={src} alt="" onError={() => setBadSrc(src)} />
      ) : (
        <span className="sh-fr-ini">{initials}</span>
      )}
      {status && <i className="sh-fr-sdot" aria-hidden />}
    </span>
  );
}

function StatusLine({ status }) {
  if (!status) return null;
  return (
    <span className={"sh-fr-st is-" + status}>
      <i className="sh-fr-stdot" aria-hidden />
      {STATUS_LABEL[status] || STATUS_LABEL.offline}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Freundesliste
// ---------------------------------------------------------------------------
export function FriendsModal({ apiBase, token, socket, onClose }) {
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [adding, setAdding] = useState(false);
  const [confirmId, setConfirmId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [rowError, setRowError] = useState(null);

  // Escape: zuerst den Such-Dialog schliessen, dann die Liste.
  const closeTop = useCallback(() => {
    if (adding) setAdding(false);
    else onClose();
  }, [adding, onClose]);
  useEscape(closeTop);

  const load = useCallback(async () => {
    try {
      const d = await call(apiBase, token, "GET", "/api/friends");
      setFriends(sortFriends(Array.isArray(d.friends) ? d.friends : []));
      setError(null);
    } catch (e) {
      setError(netError(e));
    } finally {
      setLoading(false);
    }
  }, [apiBase, token]);

  useEffect(() => {
    load();
    const t = setInterval(load, LIST_REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);

  // Live-Status. Nach einem Reconnect koennen Meldungen verpasst worden sein
  // -> dann einmal frisch laden.
  useEffect(() => {
    if (!socket) return undefined;
    const onPresence = ({ userId, status } = {}) => {
      if (!userId || !STATUS_LABEL[status]) return;
      setFriends((list) =>
        list.some((f) => f.id === userId)
          ? sortFriends(list.map((f) => (f.id === userId ? { ...f, status } : f)))
          : list
      );
    };
    const onConnect = () => load();
    socket.on(PRESENCE_EVENT, onPresence);
    socket.on("connect", onConnect);
    return () => {
      socket.off(PRESENCE_EVENT, onPresence);
      socket.off("connect", onConnect);
    };
  }, [socket, load]);

  const remove = async (f) => {
    setBusyId(f.id);
    setRowError(null);
    try {
      await call(apiBase, token, "DELETE", `/api/friends/${encodeURIComponent(f.id)}`);
      setFriends((list) => list.filter((x) => x.id !== f.id));
    } catch (e) {
      if (e.status === 404) setFriends((list) => list.filter((x) => x.id !== f.id));
      else setRowError({ id: f.id, text: netError(e) });
    } finally {
      setBusyId(null);
      setConfirmId(null);
    }
  };

  const onAdded = (friend) => {
    if (!friend || !friend.id) return;
    setFriends((list) =>
      list.some((x) => x.id === friend.id) ? list : sortFriends([...list, friend])
    );
  };

  const onlineCount = friends.filter((f) => f.status !== "offline").length;

  return (
    <>
      <div
        className="sh-sheet sh-fr-sheet"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <div
          className="sh-sheetbox sh-fr-box"
          dir="rtl"
          role="dialog"
          aria-modal="true"
          aria-label="دوستان"
        >
          <div className="sh-sheethd sh-fr-hd">
            <span className="sh-fr-hdicon">
              <FriendsIcon size={20} />
            </span>
            <h2>دوستان</h2>
            {friends.length > 0 && (
              <span className="sh-fr-count">
                {faNum(onlineCount)} آنلاین از {faNum(friends.length)}
              </span>
            )}
            <button
              className="sh-fr-plus"
              onClick={() => setAdding(true)}
              title="افزودن دوست"
              aria-label="افزودن دوست"
            >
              +
            </button>
            <button className="sh-xbtn" onClick={onClose} aria-label="بستن">
              ✕
            </button>
          </div>

          <div className="sh-fr-scroll">
            {loading ? (
              <div className="sh-fr-empty">در حال بارگیری…</div>
            ) : error ? (
              <div className="sh-fr-empty">
                <div className="sh-msg sh-msg--err">{error}</div>
                <button className="sh-btn" onClick={load} style={{ marginTop: 10 }}>
                  تلاش دوباره
                </button>
              </div>
            ) : friends.length === 0 ? (
              <div className="sh-fr-empty">
                هنوز دوستی اضافه نکرده‌ای.
                <br />
                با دکمه‌ی <b>+</b> بازیکن‌ها را پیدا کن.
              </div>
            ) : (
              <ul className="sh-fr-list">
                {friends.map((f) => (
                  <li key={f.id} className={"sh-fr-row is-" + f.status}>
                    <Ava apiBase={apiBase} user={f} size={46} status={f.status} />
                    <div className="sh-fr-main">
                      <div className="sh-fr-nm">
                        <bdi>{displayName(f)}</bdi>
                        {f.name && f.username && f.name !== f.username && (
                          <small>{f.name}</small>
                        )}
                      </div>
                      <StatusLine status={f.status} />
                      <div className="sh-fr-stats">
                        <span className="sh-fr-lv">
                          Lv {faNum(f.level)} · {f.levelTitle}
                        </span>
                        <span>امتیاز {faNum(f.pointsFor)}</span>
                        <span>
                          {faNum(f.gamesWon)} برد از {faNum(f.gamesPlayed)} بازی
                        </span>
                      </div>
                      {rowError && rowError.id === f.id && (
                        <div className="sh-msg sh-msg--err" style={{ textAlign: "start" }}>
                          {rowError.text}
                        </div>
                      )}
                    </div>
                    {confirmId === f.id ? (
                      <div className="sh-fr-confirm">
                        <span>حذف شود؟</span>
                        <button
                          className="sh-btn sh-btn--red sh-fr-sm"
                          disabled={busyId === f.id}
                          onClick={() => remove(f)}
                        >
                          {busyId === f.id ? "…" : "بله"}
                        </button>
                        <button
                          className="sh-btn sh-fr-sm"
                          disabled={busyId === f.id}
                          onClick={() => setConfirmId(null)}
                        >
                          نه
                        </button>
                      </div>
                    ) : (
                      <button
                        className="sh-fr-rm"
                        onClick={() => setConfirmId(f.id)}
                        title="حذف از دوستان"
                        aria-label={`حذف ${displayName(f)} از دوستان`}
                      >
                        ✕
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
      {adding && (
        <AddFriendDialog
          apiBase={apiBase}
          token={token}
          onClose={() => setAdding(false)}
          onAdded={onAdded}
          escapeHandled
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Suche + Hinzufuegen
// ---------------------------------------------------------------------------
export function AddFriendDialog({ apiBase, token, onClose, onAdded, escapeHandled = false }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [phase, setPhase] = useState("idle"); // idle | short | loading | done | error
  const [err, setErr] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const inputRef = useRef(null);

  // Wird der Dialog von FriendsModal geoeffnet, kuemmert sich dessen
  // Escape-Handler um die Reihenfolge; sonst selbst schliessen.
  useEscape(escapeHandled ? () => {} : onClose);

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
  }, []);

  useEffect(() => {
    const term = q.trim();
    if (term.length < SEARCH_MIN_LEN) {
      setResults([]);
      setPhase(term ? "short" : "idle");
      return undefined;
    }
    setPhase("loading");
    const ctrl = new AbortController();
    const t = setTimeout(async () => {
      try {
        const d = await call(
          apiBase,
          token,
          "GET",
          `/api/users/search?q=${encodeURIComponent(term)}`,
          undefined,
          ctrl.signal
        );
        setResults(Array.isArray(d.users) ? d.users : []);
        setErr(null);
        setPhase("done");
      } catch (e) {
        if (e?.name === "AbortError") return;
        setErr(netError(e));
        setPhase("error");
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      clearTimeout(t);
      ctrl.abort();
    };
  }, [q, apiBase, token]);

  const add = async (u) => {
    setBusyId(u.id);
    setErr(null);
    try {
      const d = await call(apiBase, token, "POST", "/api/friends", { friendId: u.id });
      setResults((rs) => rs.map((r) => (r.id === u.id ? { ...r, isFriend: true } : r)));
      if (onAdded) onAdded(d.friend);
    } catch (e) {
      if (e.status === 409) {
        setResults((rs) => rs.map((r) => (r.id === u.id ? { ...r, isFriend: true } : r)));
      } else {
        setErr(netError(e));
      }
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div
      className="sh-sheet sh-fr-sheet sh-fr-sheet--top"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="sh-sheetbox sh-fr-box sh-fr-searchbox"
        dir="rtl"
        role="dialog"
        aria-modal="true"
        aria-label="افزودن دوست"
      >
        <div className="sh-sheethd sh-fr-hd">
          <h2>افزودن دوست</h2>
          <button className="sh-xbtn" onClick={onClose} aria-label="بستن">
            ✕
          </button>
        </div>

        <div className="sh-fld sh-fr-searchfld">
          <input
            ref={inputRef}
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="نام یا نام کاربری را بنویس…"
            maxLength={40}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="none"
            spellCheck={false}
            enterKeyHint="search"
            aria-label="جستجوی بازیکن"
          />
        </div>

        <div className="sh-fr-scroll">
          {phase === "idle" && (
            <div className="sh-fr-empty">نام بازیکن را بنویس تا پیشنهادها بیایند.</div>
          )}
          {phase === "short" && (
            <div className="sh-fr-empty">حداقل {faNum(SEARCH_MIN_LEN)} حرف بنویس.</div>
          )}
          {phase === "loading" && <div className="sh-fr-empty">در حال جستجو…</div>}
          {phase === "error" && (
            <div className="sh-fr-empty">
              <div className="sh-msg sh-msg--err">{err}</div>
            </div>
          )}
          {phase === "done" && results.length === 0 && (
            <div className="sh-fr-empty">بازیکنی با این نام پیدا نشد.</div>
          )}
          {phase === "done" && results.length > 0 && (
            <ul className="sh-fr-list">
              {results.map((u) => (
                <li key={u.id} className="sh-fr-row">
                  <Ava apiBase={apiBase} user={u} size={40} />
                  <div className="sh-fr-main">
                    <div className="sh-fr-nm">
                      <bdi>{displayName(u)}</bdi>
                      {u.name && u.username && u.name !== u.username && (
                        <small>{u.name}</small>
                      )}
                    </div>
                    <div className="sh-fr-stats">
                      <span className="sh-fr-lv">
                        Lv {faNum(u.level)} · {u.levelTitle}
                      </span>
                    </div>
                  </div>
                  {u.isFriend ? (
                    <span className="sh-fr-tag">✓ دوست شما</span>
                  ) : (
                    <button
                      className="sh-btn sh-btn--gold sh-fr-sm"
                      disabled={busyId === u.id}
                      onClick={() => add(u)}
                    >
                      {busyId === u.id ? "…" : "افزودن"}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
          {phase === "done" && err && (
            <div className="sh-msg sh-msg--err">{err}</div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mini-Profil am Tisch (Tipp auf Avatar)
// ---------------------------------------------------------------------------
// `player` ist der Eintrag aus players[] des Tischs ({ userId, name,
// username, avatarUrl, ... }). Name/Foto werden sofort daraus gezeigt, Level
// und Freundschaftsstatus kommen nach.
export function PlayerProfileModal({ apiBase, token, player, meId, onClose }) {
  const userId = (player && player.userId) || null;
  const [card, setCard] = useState(null);
  const [phase, setPhase] = useState(userId ? "loading" : "guest");
  const [busy, setBusy] = useState(false);
  const [confirmRm, setConfirmRm] = useState(false);
  const [err, setErr] = useState(null);
  useEscape(onClose);

  useEffect(() => {
    if (!userId) {
      setPhase("guest");
      return undefined;
    }
    let alive = true;
    const ctrl = new AbortController();
    setPhase("loading");
    call(apiBase, token, "GET", `/api/users/${encodeURIComponent(userId)}/card`, undefined, ctrl.signal)
      .then((d) => {
        if (!alive) return;
        setCard(d.user || null);
        setPhase(d.user ? "ready" : "guest");
      })
      .catch((e) => {
        if (!alive || e?.name === "AbortError") return;
        if (e.status === 404) setPhase("guest");
        else {
          setErr(netError(e));
          setPhase("error");
        }
      });
    return () => {
      alive = false;
      ctrl.abort();
    };
  }, [apiBase, token, userId]);

  // Eigener Avatar: NIE ein Hinzufuegen-Button - auch nicht, solange die
  // Karte noch laedt.
  const isSelf = !!userId && (userId === meId || !!card?.isSelf);
  const isGuest = phase === "guest";
  const shown = { ...(player || {}), ...(card || {}) };

  const add = async () => {
    setBusy(true);
    setErr(null);
    try {
      await call(apiBase, token, "POST", "/api/friends", { friendId: userId });
      setCard((c) => ({ ...(c || {}), isFriend: true }));
    } catch (e) {
      if (e.status === 409) setCard((c) => ({ ...(c || {}), isFriend: true }));
      else setErr(netError(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    setErr(null);
    try {
      await call(apiBase, token, "DELETE", `/api/friends/${encodeURIComponent(userId)}`);
      setCard((c) => ({ ...(c || {}), isFriend: false, status: null }));
    } catch (e) {
      if (e.status === 404) setCard((c) => ({ ...(c || {}), isFriend: false, status: null }));
      else setErr(netError(e));
    } finally {
      setBusy(false);
      setConfirmRm(false);
    }
  };

  return (
    <div
      className="sh-sheet sh-fr-sheet"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="sh-sheetbox sh-fr-box sh-fr-cardbox"
        dir="rtl"
        role="dialog"
        aria-modal="true"
        aria-label="پروفایل بازیکن"
      >
        <div className="sh-sheethd sh-fr-hd">
          <h2>پروفایل بازیکن</h2>
          <button className="sh-xbtn" onClick={onClose} aria-label="بستن">
            ✕
          </button>
        </div>

        <div className="sh-fr-card">
          <Ava
            apiBase={apiBase}
            user={shown}
            size={96}
            status={card?.isFriend ? card.status || null : null}
          />
          <div className="sh-fr-cardnm">{displayName(shown)}</div>
          {shown.name && shown.username && shown.name !== shown.username && (
            <div className="sh-fr-cardsub">{shown.name}</div>
          )}

          {phase === "loading" && <div className="sh-fr-cardsub">در حال بارگیری…</div>}

          {phase === "ready" && card && (
            <>
              <span className="sh-fr-lvpill">
                Lv {faNum(card.level)} · {card.levelTitle}
              </span>
              <div className="sh-fr-cardsub">
                {faNum(card.gamesWon)} برد از {faNum(card.gamesPlayed)} بازی
              </div>
              {card.isFriend && card.status && <StatusLine status={card.status} />}
            </>
          )}

          {isGuest && <div className="sh-fr-cardsub">مهمان (بدون حساب کاربری)</div>}
          {phase === "error" && <div className="sh-msg sh-msg--err">{err}</div>}

          <div className="sh-fr-cardact">
            {isSelf ? (
              <span className="sh-fr-tag">این تو هستی</span>
            ) : phase !== "ready" || !card ? null : card.isFriend ? (
              confirmRm ? (
                <div className="sh-fr-confirm">
                  <span>حذف از دوستان؟</span>
                  <button className="sh-btn sh-btn--red sh-fr-sm" disabled={busy} onClick={remove}>
                    {busy ? "…" : "بله"}
                  </button>
                  <button
                    className="sh-btn sh-fr-sm"
                    disabled={busy}
                    onClick={() => setConfirmRm(false)}
                  >
                    نه
                  </button>
                </div>
              ) : (
                <>
                  <span className="sh-fr-tag">✓ دوست شما</span>
                  <button className="sh-btn sh-fr-sm" onClick={() => setConfirmRm(true)}>
                    حذف از دوستان
                  </button>
                </>
              )
            ) : (
              <button className="sh-btn sh-btn--gold" disabled={busy} onClick={add}>
                {busy ? "…" : "افزودن به دوستان"}
              </button>
            )}
          </div>
          {phase === "ready" && err && <div className="sh-msg sh-msg--err">{err}</div>}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Privatsphaere im Profil
// ---------------------------------------------------------------------------
export function OnlineVisibilityToggle({ apiBase, token }) {
  const [on, setOn] = useState(null); // null = noch unbekannt
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!token) return undefined;
    let alive = true;
    call(apiBase, token, "GET", "/api/me/privacy")
      .then((d) => alive && setOn(d.showOnline !== false))
      .catch((e) => alive && setErr(netError(e)));
    return () => {
      alive = false;
    };
  }, [apiBase, token]);

  const toggle = async () => {
    if (on === null || busy) return;
    const next = !on;
    setOn(next);
    setBusy(true);
    setErr(null);
    try {
      const d = await call(apiBase, token, "PUT", "/api/me/privacy", { showOnline: next });
      setOn(d.showOnline !== false);
    } catch (e) {
      setOn(!next);
      setErr(netError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <fieldset className="sh-fset">
      <legend>حریم خصوصی</legend>
      <div
        className={"sh-switchrow" + (on === null || busy ? " is-locked" : "")}
        role="switch"
        aria-checked={!!on}
        aria-disabled={on === null || busy}
        tabIndex={0}
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggle();
          }
        }}
      >
        <span className="sh-txt">
          نمایش وضعیت آنلاین من
          <small>اگر خاموش باشد، دوستانت تو را همیشه آفلاین می‌بینند.</small>
        </span>
        <span className={"sh-sw" + (on ? " is-on" : "")} />
      </div>
      {err && <div className="sh-msg sh-msg--err">{err}</div>}
    </fieldset>
  );
}
